import React, { useState } from "react";
import { Box, Chip, IconButton, Tooltip, alpha } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import TechnicalIssueDetailDialog from "./TechnicalIssueDetailDialog";
import { deleteReport } from "../services/technicalIssuesStorageService";

const SEVERITY_COLORS = {
  critical: { bg: "#FEE2E2", color: "#991B1B" },
  high: { bg: "#FFEDD5", color: "#9A3412" },
  medium: { bg: "#FEF9C3", color: "#854D0E" },
  low: { bg: "#DBEAFE", color: "#1E40AF" },
};

const STATUS_COLORS = {
  open: { bg: "#FEE2E2", color: "#991B1B" },
  in_progress: { bg: "#DBEAFE", color: "#1E40AF" },
  resolved: { bg: "#D1FAE5", color: "#065F46" },
};

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
};

export default function TechnicalIssueList({ reports, onRefresh }) {
  const [selectedReport, setSelectedReport] = useState(null);

  const handleDelete = (e, id) => {
    e.stopPropagation();
    deleteReport(id);
    if (onRefresh) onRefresh();
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Date",
      width: 130,
      valueFormatter: (value) => dayjs(value).format("MMM D, YYYY"),
    },
    {
      field: "module",
      headerName: "Module",
      width: 160,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 500,
            bgcolor: alpha("#6366F1", 0.1),
            color: "#4338CA",
          }}
        />
      ),
    },
    {
      field: "severity",
      headerName: "Severity",
      width: 110,
      renderCell: (params) => {
        const cfg = SEVERITY_COLORS[params.value] || SEVERITY_COLORS.medium;
        return (
          <Chip
            label={params.value?.charAt(0).toUpperCase() + params.value?.slice(1)}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.75rem",
              fontWeight: 600,
              bgcolor: cfg.bg,
              color: cfg.color,
            }}
          />
        );
      },
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const cfg = STATUS_COLORS[params.value] || STATUS_COLORS.open;
        return (
          <Chip
            label={STATUS_LABELS[params.value] || params.value}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.75rem",
              fontWeight: 600,
              bgcolor: cfg.bg,
              color: cfg.color,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={(e) => handleDelete(e, params.row.id)}
            sx={{ color: "error.main" }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={reports}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
        onRowClick={(params) => setSelectedReport(params.row)}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          "& .MuiDataGrid-row": {
            cursor: "pointer",
            "&:hover": {
              bgcolor: alpha("#EF4444", 0.04),
            },
          },
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: alpha("#F97316", 0.05),
          },
        }}
      />

      <TechnicalIssueDetailDialog
        report={selectedReport}
        open={!!selectedReport}
        onClose={() => setSelectedReport(null)}
      />
    </Box>
  );
}
